import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Client Portal auth — completely separate from the admin /AuthContext.
 * Token lives in an httpOnly `portal_token` cookie set by the backend.
 * On boot we probe `/portal/me` to hydrate the session silently.
 */
const PortalAuthContext = createContext({
  client: undefined, // undefined = unknown, null = anonymous, object = signed in
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
});

export function PortalAuthProvider({ children }) {
  const [client, setClient] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/me");
      setClient(data);
    } catch {
      setClient(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/portal/auth/login", { email, password });
    setClient(data.client);
    return data.client;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/portal/auth/register", payload);
    setClient(data.client);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/portal/auth/logout"); } catch { /* ignore */ }
    setClient(null);
  }, []);

  return (
    <PortalAuthContext.Provider value={{ client, login, register, logout, refresh }}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth() {
  return useContext(PortalAuthContext);
}
